import { createTheme } from '@mui/material/styles';

/**
 * Full Frame Design System — Stitch-inspired theme
 *
 * Palette: cool slate, muted purple accent, neutral backgrounds
 * Typography: Manrope (headlines) + Inter (body)
 */

const primary = '#526075';
const primaryDim = '#465469';
const primaryContainer = '#d5e3fd';
const tertiary = '#635983';
const tertiaryContainer = '#dbcdff';
const slate = '#2d3335';
const secondary = '#556071';
const outline = '#767c7e';
const outlineVariant = '#adb3b5';
const line = 'rgba(45, 51, 53, 0.08)';
const bg = '#f8f9fa';
const surface = '#ffffff';
const surfaceLow = '#f1f4f5';

export const theme = createTheme({
  palette: {
    primary: { main: primary, light: primaryContainer, dark: primaryDim, contrastText: '#f8f8ff' },
    secondary: { main: secondary, light: '#8a949c', dark: '#3d4750', contrastText: '#fff' },
    error: { main: '#a83836', light: '#fef2f0' },
    warning: { main: '#d4943a', light: '#fef8ee' },
    success: { main: '#10b981', light: '#f0fdf4' },
    info: { main: secondary, light: surfaceLow },
    background: { default: bg, paper: surface },
    text: { primary: slate, secondary: secondary, disabled: outlineVariant },
    divider: line,
  },
  typography: {
    fontFamily: '"Inter", "Plus Jakarta Sans", system-ui, sans-serif',
    h1: { fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 800, letterSpacing: '-0.03em' },
    h2: { fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 700, letterSpacing: '-0.01em' },
    h4: { fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 700 },
    h5: { fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 700 },
    h6: { fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 600 },
    subtitle1: { fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 700 },
    subtitle2: { fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 600 },
    button: { fontWeight: 600, textTransform: 'none' },
    caption: { fontWeight: 600, fontSize: '0.75rem', letterSpacing: '0.04em' },
  },
  shape: { borderRadius: 12 },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(45,51,53,0.04)',
    '0 1px 3px 0 rgba(45,51,53,0.06)',
    '0 4px 6px -1px rgba(45,51,53,0.06)',
    '0 4px 6px -1px rgba(45,51,53,0.06)',
    '0 10px 15px -3px rgba(45,51,53,0.06)',
    '0 10px 15px -3px rgba(45,51,53,0.06)',
    '0 10px 30px rgba(45,51,53,0.04)',
    ...Array(17).fill('0 10px 30px rgba(45,51,53,0.06)'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: { body: { backgroundColor: bg, WebkitFontSmoothing: 'antialiased' } },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 10px 30px rgba(45,51,53,0.04)',
          borderBottom: 'none',
          borderRadius: 0,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: '0.5rem', padding: '0.6rem 1.25rem', fontWeight: 600, fontSize: '0.875rem', transition: 'all 0.2s ease' },
        contained: {
          boxShadow: '0 4px 8px -2px rgba(82,96,117,0.2)',
          '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 12px -2px rgba(82,96,117,0.3)' },
        },
        containedPrimary: { background: `linear-gradient(135deg, ${primary} 0%, ${primaryDim} 100%)` },
        outlined: { borderWidth: '1px', borderColor: line, '&:hover': { borderWidth: '1px', backgroundColor: surfaceLow } },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '0.75rem', border: 'none',
          boxShadow: '0 1px 3px 0 rgba(45,51,53,0.04)',
          transition: 'box-shadow 0.2s ease',
          '&:hover': { boxShadow: '0 10px 30px rgba(45,51,53,0.06)' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: '0.75rem' },
        elevation1: { boxShadow: '0 1px 3px 0 rgba(45,51,53,0.04)' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '0.5rem',
            '& fieldset': { borderWidth: '1px', borderColor: line },
            '&:hover fieldset': { borderColor: outlineVariant },
            '&.Mui-focused fieldset': { borderColor: primary, borderWidth: '1.5px', boxShadow: '0 0 0 3px rgba(82,96,117,0.1)' },
          },
        },
      },
    },
    MuiSelect: { styleOverrides: { root: { borderRadius: '0.5rem' } } },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: '0.75rem', borderRadius: '9999px' },
        colorPrimary: { backgroundColor: 'rgba(82,96,117,0.08)', color: primary },
        colorSecondary: { backgroundColor: surfaceLow, color: secondary },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: slate, borderRadius: '0.375rem', fontSize: '0.8125rem', padding: '0.4rem 0.65rem' },
        arrow: { color: slate },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: '0.75rem', boxShadow: '0 20px 40px -8px rgba(45,51,53,0.15)' } },
    },
    MuiFab: {
      styleOverrides: {
        root: { boxShadow: '0 4px 12px -2px rgba(82,96,117,0.25)' },
        primary: {
          background: `linear-gradient(135deg, ${primary} 0%, ${primaryDim} 100%)`,
          '&:hover': { background: `linear-gradient(135deg, ${primaryDim} 0%, #3a4857 100%)` },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: '9999px', backgroundColor: 'rgba(45,51,53,0.08)' },
        bar: { borderRadius: '9999px', backgroundColor: primary },
      },
    },
    MuiCircularProgress: { styleOverrides: { colorPrimary: { color: primary } } },
    MuiTabs: { styleOverrides: { indicator: { backgroundColor: primary, height: 3, borderRadius: '3px 3px 0 0' } } },
    MuiTab: { styleOverrides: { root: { fontWeight: 600, '&.Mui-selected': { color: slate } } } },
    MuiSwitch: { styleOverrides: { switchBase: { '&.Mui-checked': { color: primary, '& + .MuiSwitch-track': { backgroundColor: primaryContainer } } } } },
    MuiCheckbox: { styleOverrides: { root: { color: outlineVariant, '&.Mui-checked': { color: primary } } } },
    MuiRadio: { styleOverrides: { root: { color: outlineVariant, '&.Mui-checked': { color: primary } } } },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: '0.5rem', border: '1px solid' },
        standardError: { backgroundColor: '#fef2f0', borderColor: '#f5c4c0', color: '#a83836' },
        standardSuccess: { backgroundColor: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' },
        standardWarning: { backgroundColor: '#fef8ee', borderColor: '#f5deb0', color: '#8a6818' },
        standardInfo: { backgroundColor: surfaceLow, borderColor: line, color: secondary },
      },
    },
  },
});
